import React from "react";
import { cn } from "../../lib/utils.js";
import type { DataTableColumn } from "./DataTable.js";
import { resolveCardColumns } from "./resolve-card-columns.js";

/* ---------------- DataTableCardSummary ----------------
   The table's footer row in the `responsive` card list (below `md`): one
   trailing card after the row cards. It is not a row card, so sorting, which
   reorders the cards, cannot move it, and DataTable mounts it only while the
   cards show data (the footer row's own rule).

   Placement mirrors a row card. The card title column's footer (the "Total"
   label, typically) heads it. Every other column that sets a footer is a
   labelled value in column order, headed by that column's header, because a
   bare "381" would announce no idea of what. A column without a footer is
   left out, so the summary never prints an empty label.

   Split from DataTableCards.tsx, which sits at the package's 200-line soft
   cap (§6). Same slot rules, same meta typography, plus the footer's weight. */

export interface DataTableCardSummaryProps<Row> {
  readonly columns: ReadonlyArray<DataTableColumn<Row>>;
}

export function DataTableCardSummary<Row>({
  columns,
}: DataTableCardSummaryProps<Row>): React.ReactElement {
  const { title } = resolveCardColumns(columns).slots;
  const hasHeading = title?.footer !== undefined;
  const values = columns.filter(
    (col) => col !== title && col.footer !== undefined,
  );

  return (
    <div className="mt-2 md:hidden">
      <div
        data-card-summary=""
        className="rounded-lg border border-pb-border bg-pb-card p-3"
      >
        {hasHeading && (
          <div className="text-sm font-medium text-pb-text">
            {title?.footer}
          </div>
        )}
        {values.length > 0 && (
          <dl
            className={cn(
              "grid grid-cols-2 gap-x-4 gap-y-2",
              hasHeading && "mt-3 border-t border-pb-border pt-3",
            )}
          >
            {values.map((col) => (
              <div key={col.id} className="min-w-0">
                <dt className="text-dense-2 uppercase tracking-wider text-pb-text-muted">
                  {col.header}
                </dt>
                <dd
                  className={cn(
                    "text-sm font-medium text-pb-text",
                    col.align === "end" && "pb-tabular",
                  )}
                >
                  {col.footer}
                </dd>
              </div>
            ))}
          </dl>
        )}
      </div>
    </div>
  );
}
